import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tabs,
  Tab,
  Card,
  CardContent,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Call as CallIcon,
  Sms as SmsIcon,
  Message as MessageIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  PlayArrow as ResumeIcon,
  DeleteForever as DeleteForeverIcon,
  VerifiedUser as SafelistIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  MenuBook as MenuBookIcon,
  ContactPhone as ContactPhoneIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import AccessOverridePanel from '../components/AccessOverridePanel';
import UserIngestionPanel from '../components/UserIngestionPanel';
import ScheduledTrainingPanel from '../components/ScheduledTrainingPanel';

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Currency formatter
const formatCurrency = (amount, currency = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount || 0);
};

// Stat card component
const StatCard = ({ icon, label, value, subValue, color = 'primary' }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ color: `${color}.main`, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" fontWeight={600}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {subValue && (
        <Typography variant="caption" color="text.secondary">{subValue}</Typography>
      )}
    </CardContent>
  </Card>
);

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [userData, setUserData] = useState(null);
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [adminNotes, setAdminNotes] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityCategory, setActivityCategory] = useState('');
  const [activityDetailDialog, setActivityDetailDialog] = useState(null);
  
  // Billing & Loyalty
  const [billingHistory, setBillingHistory] = useState({ charges: [], refunds: [], invoices: [] });
  const [billingLoading, setBillingLoading] = useState(false);
  const [loyaltyStats, setLoyaltyStats] = useState(null);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [notifyUser, setNotifyUser] = useState(true);
  
  const [subscriptionDialog, setSubscriptionDialog] = useState(false);
  const [newQuantity, setNewQuantity] = useState(1);
  const [quantityReason, setQuantityReason] = useState('');
  
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  
  const [noteDialog, setNoteDialog] = useState(false);
  const [newNote, setNewNote] = useState('');

  // Client management dialogs
  const [addClientDialog, setAddClientDialog] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [newClientData, setNewClientData] = useState({ name: '', phone: '', email: '' });
  const [addClientMode, setAddClientMode] = useState('search');
  
  const [removeClientDialog, setRemoveClientDialog] = useState(null);
  const [sendingGuide, setSendingGuide] = useState(null);
  const [sendingVCard, setSendingVCard] = useState(null);

  // Delete account
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Trial management
  const [trialDialog, setTrialDialog] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState('');
  const [trialReason, setTrialReason] = useState('');


  useEffect(() => {
    loadUser();
  }, [userId]);

  useEffect(() => {
    if (tabValue === 1 && activity.length === 0) {
      loadActivity();
    }
    if (tabValue === 3 && billingHistory.charges.length === 0) {
      loadBillingHistory();
    }
  }, [tabValue]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUser(userId);
      setUserData(response.data.user);
      setClients(response.data.clients || []);
      setCaregivers(response.data.caregivers || []);
      setPendingVerifications(response.data.pendingVerifications || []);
      setAdminNotes(response.data.adminNotes || []);
      setAuditLog(response.data.auditLog || []);
      setEditData(response.data.user);
      
      // Load loyalty stats immediately
      loadLoyaltyStats();
    } catch (err) {
      console.error('Failed to load user:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async (cat) => {
    try {
      setActivityLoading(true);
      const params = {};
      const catValue = cat !== undefined ? cat : activityCategory;
      if (catValue) params.category = catValue;
      const response = await adminAPI.getUserActivity(userId, params);
      setActivity(response.data.activity || []);
    } catch (err) {
      console.error('Failed to load activity:', err);
    } finally {
      setActivityLoading(false);
    }
  };

  const loadBillingHistory = async () => {
    try {
      setBillingLoading(true);
      const response = await adminAPI.getBillingHistory(userId);
      setBillingHistory({
        charges: response.data.charges || [],
        refunds: response.data.refunds || [],
        invoices: response.data.invoices || [],
      });
    } catch (err) {
      console.error('Failed to load billing history:', err);
    } finally {
      setBillingLoading(false);
    }
  };

  const loadLoyaltyStats = async () => {
    try {
      setLoyaltyLoading(true);
      const response = await adminAPI.getLoyaltyStats(userId);
      setLoyaltyStats(response.data.stats);
    } catch (err) {
      console.error('Failed to load loyalty stats:', err);
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminAPI.updateUser(userId, {
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
        is_active: editData.is_active,
        email_verified: editData.email_verified,
        phone_verified: editData.phone_verified,
        is_super_admin: editData.is_super_admin,
        role: editData.role,
        account_type: editData.account_type,
      });
      setSuccess('User updated successfully');
      setEditing(false);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleSendPasswordReset = async () => {
    try {
      await adminAPI.sendPasswordReset(userId);
      setSuccess('Password reset email sent');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send password reset');
    }
  };

  const handleSetTempPassword = async () => {
    try {
      await adminAPI.setTemporaryPassword(userId, tempPassword, notifyUser);
      setSuccess('Temporary password set');
      setPasswordDialog(false);
      setTempPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set password');
    }
  };

  const handleUpdateQuantity = async () => {
    try {
      await adminAPI.updateQuantity(userId, parseInt(newQuantity), quantityReason);
      setSuccess('Subscription quantity updated');
      setSubscriptionDialog(false);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quantity');
    }
  };

  const handleRefund = async () => {
    try {
      const amount = refundAmount ? parseFloat(refundAmount) : undefined;
      const response = await adminAPI.issueRefund(userId, amount, refundReason);
      setSuccess(`Refund of ${formatCurrency(response.data.refund.amount)} issued`);
      setRefundDialog(false);
      setRefundAmount('');
      setRefundReason('');
      loadBillingHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to issue refund');
    }
  };

  const handleCancelSubscription = async (immediately) => {
    if (!window.confirm(`Are you sure you want to cancel this subscription${immediately ? ' immediately' : ''}?`)) {
      return;
    }
    try {
      await adminAPI.cancelSubscription(userId, immediately, 'Admin action');
      setSuccess('Subscription canceled');
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel subscription');
    }
  };

  const handleUpdateTrialEnd = async () => {
    try {
      if (!trialEndDate) {
        setError('Please select a trial end date');
        return;
      }
      await adminAPI.updateTrialEnd(userId, trialEndDate);
      setSuccess('Trial period updated');
      setTrialDialog(false);
      setTrialEndDate('');
      setTrialReason('');
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update trial');
    }
  };

  const handleManuallyVerify = async (clientId) => {
    try {
      await adminAPI.manuallyVerify(userId, clientId, 'Admin manual verification');
      setSuccess('Client manually verified');
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify client');
    }
  };

  const handleAddNote = async () => {
    try {
      await adminAPI.addNote(userId, newNote);
      setSuccess('Note added');
      setNoteDialog(false);
      setNewNote('');
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add note');
    }
  };

  const handleExport = async () => {
    try {
      const response = await adminAPI.exportUserData(userId);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-export-${userId}.json`;
      a.click();
      setSuccess('Data exported');
    } catch (err) {
      setError('Failed to export data');
    }
  };

  // Suspend / unsuspend account
  const handleToggleSuspend = async () => {
    const newStatus = !userData.is_active;
    const action = newStatus ? 'unsuspend' : 'suspend';
    if (!window.confirm(`Are you sure you want to ${action} this account?`)) return;
    try {
      await adminAPI.updateUser(userId, { is_active: newStatus });
      setSuccess(`Account ${action}ed successfully`);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} account`);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    try {
      setDeleting(true);
      await adminAPI.deleteUser(userId, deleteReason);
      setDeleteDialog(false);
      setSuccess('Account deleted and data anonymized');
      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account');
      setDeleting(false);
    }
  };


  // Client management functions
  const handleSearchClients = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setClientSearchResults([]);
      return;
    }
    try {
      setClientSearchLoading(true);
      const response = await adminAPI.searchUsers(searchTerm);
      const existingClientIds = clients.map(s => s.id);
      const filtered = response.data.users.filter(u => 
        u.id !== userId && !existingClientIds.includes(u.id)
      );
      setClientSearchResults(filtered);
    } catch (err) {
      console.error('Client search failed:', err);
    } finally {
      setClientSearchLoading(false);
    }
  };

  const handleLinkClient = async (clientId) => {
    try {
      await adminAPI.linkClient(userId, clientId);
      setSuccess('Client linked successfully');
      setAddClientDialog(false);
      setClientSearch('');
      setClientSearchResults([]);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link client');
    }
  };

  const handleCreateAndLinkClient = async () => {
    try {
      if (!newClientData.name || !newClientData.phone) {
        setError('Name and phone are required');
        return;
      }
      await adminAPI.createAndLinkClient(userId, newClientData);
      setSuccess('Client created and linked successfully');
      setAddClientDialog(false);
      setNewClientData({ name: '', phone: '', email: '' });
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create client');
    }
  };

  const handleUnlinkClient = async () => {
    if (!removeClientDialog) return;
    try {
      // When viewing a client's page, the page userId is the client and
      // removeClientDialog contains the caregiver — swap params so the API
      // receives (caregiverId, clientId) as expected.
      const isClientPage = caregivers.length > 0;
      const caregiverId = isClientPage ? removeClientDialog.id : userId;
      const clientId = isClientPage ? userId : removeClientDialog.id;
      await adminAPI.unlinkClient(caregiverId, clientId);
      setSuccess('Client unlinked successfully');
      setRemoveClientDialog(null);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlink client');
    }
  };

  const getEventIcon = (type, category) => {
    // Lifecycle categories
    if (category && category !== 'analysis') {
      switch (category) {
        case 'account': return <LockIcon fontSize="small" />;
        case 'subscription': return <MoneyIcon fontSize="small" />;
        case 'clients': return <PersonAddIcon fontSize="small" />;
        case 'admin': return <AdminIcon fontSize="small" />;
        case 'safelist': return <SafelistIcon fontSize="small" />;
        case 'training': return <ShieldIcon fontSize="small" />;
        default: return <MessageIcon fontSize="small" />;
      }
    }
    // Analysis events
    switch (type) {
      case 'call':
      case 'voice':
        return <CallIcon fontSize="small" />;
      case 'sms':
      case 'text':
        return <SmsIcon fontSize="small" />;
      default:
        return <MessageIcon fontSize="small" />;
    }
  };

  const getConcernColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'error';
      case 'moderate':
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getConcernLabel = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'High Concern';
      case 'moderate':
      case 'medium': return 'Moderate Concern';
      case 'low': return 'Low Concern';
      default: return level || '';
    }
  };

  // Uniform panel layout for Details tab
  const cardStyle = { p: 3, height: '100%', display: 'flex', flexDirection: 'column' };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!userData) {
    return <Alert severity="error">User not found</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/users')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" fontWeight={600}>
              {userData.name || userData.email}
            </Typography>
            {userData.is_super_admin && (
              <Chip icon={<AdminIcon />} label="Super Admin" color="secondary" size="small" />
            )}
            {caregivers.length > 0 && (
              <Chip icon={<LinkIcon />} label="Managed Client" color="info" size="small" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {userData.id}
          </Typography>
        </Box>
        {!editing ? (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => { setEditing(false); setEditData(userData); }}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Loyalty Stats Banner */}
      {loyaltyStats && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <StarIcon sx={{ fontSize: 40 }} />
            </Grid>
            <Grid item xs>
              <Typography variant="h6">
                {caregivers.length > 0 ? 'Managed client' : 'Customer'} for {loyaltyStats.accountAge}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {loyaltyStats.totalInquiries} inquiries evaluated • {loyaltyStats.highRiskDetected} high-concern threats detected • {formatCurrency(loyaltyStats.totalPaid)} lifetime value
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Details" />
          <Tab label={`Activity (${activity.length || '...'})`} />
          <Tab label="Admin Notes" />
          <Tab label="Billing History" />
          <Tab label="Scheduled Training" />
        </Tabs>
      </Paper>

      {/* Details Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Loyalty Stats Cards */}
        {loyaltyStats && (() => {
          const topChannelIsTexts = (loyaltyStats.totalTexts || 0) >= (loyaltyStats.totalCalls || 0);
          const topChannelValue = topChannelIsTexts ? loyaltyStats.totalTexts : loyaltyStats.totalCalls;
          const topChannelLabel = topChannelIsTexts ? 'Texts' : 'Calls';
          const TopChannelIcon = topChannelIsTexts ? SmsIcon : CallIcon;
          return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<MessageIcon />}
                label="Total Inquiries"
                value={loyaltyStats.totalInquiries}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<MessageIcon />}
                label="Last 30 Days"
                value={loyaltyStats.inquiriesLast30Days || 0}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<TopChannelIcon />}
                label={`Top Channel`}
                value={`${topChannelValue} ${topChannelLabel}`}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<WarningIcon />}
                label="High Concern"
                value={loyaltyStats.highRiskDetected}
                color="error"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<ShieldIcon />}
                label="Days Protected"
                value={loyaltyStats.accountAgeDays}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<MoneyIcon />}
                label="Lifetime Value"
                value={formatCurrency(loyaltyStats.totalPaid)}
                color="success"
              />
            </Grid>
          </Grid>
          );
        })()}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {/* User Details */}
          <Paper sx={cardStyle}>
            <Typography variant="h6" gutterBottom>User Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={editing ? editData.name || '' : userData.name || ''}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    disabled={!editing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={editing ? editData.email || '' : userData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    disabled={!editing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={editing ? editData.phone || '' : userData.phone || ''}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    disabled={!editing}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth disabled={!editing}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={editing ? editData.role || '' : userData.role || ''}
                      label="Role"
                      onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                    >
                      <MenuItem value="caregiver">Caregiver</MenuItem>
                      <MenuItem value="client">Client</MenuItem>
                      <MenuItem value="both">Both</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth disabled={!editing}>
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={editing ? editData.account_type || '' : userData.account_type || ''}
                      label="Account Type"
                      onChange={(e) => setEditData({ ...editData, account_type: e.target.value })}
                    >
                      <MenuItem value="owner">Owner</MenuItem>
                      <MenuItem value="client_solo">Client Solo</MenuItem>
                      <MenuItem value="client">Client</MenuItem>
                      <MenuItem value="invited">Invited</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editing ? editData.is_active : userData.is_active}
                        onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                        disabled={!editing}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editing ? editData.email_verified : userData.email_verified}
                        onChange={(e) => setEditData({ ...editData, email_verified: e.target.checked })}
                        disabled={!editing}
                      />
                    }
                    label="Email Verified"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editing ? editData.phone_verified : userData.phone_verified}
                        onChange={(e) => setEditData({ ...editData, phone_verified: e.target.checked })}
                        disabled={!editing}
                      />
                    }
                    label="Phone Verified"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editing ? editData.is_super_admin : userData.is_super_admin}
                        onChange={(e) => setEditData({ ...editData, is_super_admin: e.target.checked })}
                        disabled={!editing}
                        color="secondary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AdminIcon fontSize="small" color={userData.is_super_admin ? 'secondary' : 'disabled'} />
                        Super Admin
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary">
                Registered via: {userData.registered_via || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Created: {userData.created_at ? format(new Date(userData.created_at), 'PPpp') : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last Login: {userData.last_login ? format(new Date(userData.last_login), 'PPpp') : 'Never'}
              </Typography>
          </Paper>

          {/* Quick Actions */}
          <Paper sx={cardStyle}>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Grid container spacing={1}>
                {userData.email && (
                  <>
                    <Grid item xs={6}>
                      <Button fullWidth variant="outlined" startIcon={<EmailIcon />} onClick={handleSendPasswordReset}>
                        Send Reset Email
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button fullWidth variant="outlined" startIcon={<LockIcon />} onClick={() => setPasswordDialog(true)}>
                        Set Password
                      </Button>
                    </Grid>
                  </>
                )}
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setNoteDialog(true)}>
                    Add Note
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
                    Export Data
                  </Button>
                </Grid>
                {userData.phone && (
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={sendingGuide === userData.id ? <CircularProgress size={18} /> : <MenuBookIcon />}
                      disabled={sendingGuide === userData.id}
                      onClick={async () => {
                        setSendingGuide(userData.id);
                        try {
                          await adminAPI.sendGuideToUser(userId);
                          setSuccess(`Quick start guide sent to ${userData.name || userData.phone}`);
                          setTimeout(() => setSuccess(''), 5000);
                        } catch (err) {
                          setError(err.response?.data?.error || 'Failed to send guide');
                        } finally {
                          setSendingGuide(null);
                        }
                      }}
                    >
                      Send Guide
                    </Button>
                  </Grid>
                )}
                {userData.phone && (
                  <Grid item xs={6}>
                    <Tooltip title={userData.vcard_sent_at ? `vCard last sent ${format(new Date(userData.vcard_sent_at), 'MMM d, yyyy')}` : 'Never sent'}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={sendingVCard === userData.id ? <CircularProgress size={18} /> : <ContactPhoneIcon />}
                        disabled={sendingVCard === userData.id}
                        onClick={async () => {
                          setSendingVCard(userData.id);
                          try {
                            await adminAPI.sendVCardToUser(userId);
                            setSuccess(`Contact card sent to ${userData.name || userData.phone}`);
                            loadUser();
                            setTimeout(() => setSuccess(''), 5000);
                          } catch (err) {
                            setError(err.response?.data?.error || 'Failed to send contact card');
                          } finally {
                            setSendingVCard(null);
                          }
                        }}
                      >
                        Send vCard
                      </Button>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Account Actions</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color={userData.is_active ? 'warning' : 'success'}
                    startIcon={userData.is_active ? <BlockIcon /> : <ResumeIcon />}
                    onClick={handleToggleSuspend}
                  >
                    {userData.is_active ? 'Suspend' : 'Unsuspend'}
                  </Button>
                </Grid>
                {caregivers.length > 0 && (
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="warning"
                      startIcon={<LinkIcon />}
                      onClick={() => setRemoveClientDialog(caregivers[0])}
                    >
                      Unlink from Caregiver
                    </Button>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => setDeleteDialog(true)}
                    disabled={!!userData.deleted_at}
                  >
                    {userData.deleted_at ? 'Deleted' : 'Delete Account'}
                  </Button>
                </Grid>
              </Grid>
          </Paper>

          {/* Subscription or Managed By */}
          {caregivers.length > 0 ? (
            <Paper sx={cardStyle}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon color="info" />
                Managed By
              </Typography>
              {caregivers.map((cg) => {
                const activeStatuses = ['active', 'trialing', 'past_due'];
                const userSubActive = activeStatuses.includes(userData.subscription_status) || activeStatuses.includes(cg.subscription_status);
                return (
                  <Box key={cg.id}>
                    <Typography variant="h6" fontWeight={500}>{cg.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{cg.email}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Subscription:</Typography>
                      <Chip
                        label={userSubActive
                          ? `Active (Qty: ${cg.subscription_quantity || 1})`
                          : cg.subscription_status || 'None'}
                        size="small"
                        color={userSubActive ? 'success' : 'default'}
                      />
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: userSubActive ? '#f0fdf4' : '#fef2f2' }}>
                      <Typography variant="body2" color={userSubActive ? 'success.main' : 'error.main'}>
                        {userSubActive
                          ? 'This client is covered under an active subscription'
                          : 'No active subscription found — service may be limited'}
                      </Typography>
                    </Paper>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => navigate(`/users/${cg.id}`)}
                    >
                      View Caregiver Account
                    </Button>
                  </Box>
                );
              })}
            </Paper>
          ) : (
            <Paper sx={cardStyle}>
              <Typography variant="h6" gutterBottom>Subscription</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  label={userData.subscription_status || 'None'}
                  color={userData.subscription_status === 'active' ? 'success' : 'default'}
                />
                <Typography>Qty: {userData.subscription_quantity || 0}</Typography>
              </Box>
              {userData.stripe_customer_id && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Stripe: {userData.stripe_customer_id}
                </Typography>
              )}
              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <Button fullWidth size="small" variant="outlined" onClick={() => { setNewQuantity(userData.subscription_quantity || 1); setSubscriptionDialog(true); }}>
                    Change Qty
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button fullWidth size="small" variant="outlined" color="info" onClick={() => setTrialDialog(true)}>
                    {userData.subscription_status === 'trialing' ? 'Manage Trial' : 'Start Trial'}
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button fullWidth size="small" variant="outlined" color="warning" onClick={() => handleCancelSubscription(false)}>
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button fullWidth size="small" variant="outlined" color="error" onClick={() => setRefundDialog(true)}>
                    Refund
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Access Override */}
          <AccessOverridePanel
            userId={userId}
            userData={userData}
            onUpdate={(updatedData) => setUserData({ ...userData, ...updatedData })}
            sx={cardStyle}
          />

          {/* User Ingestion Control */}
          <UserIngestionPanel
            userId={userId}
            userData={userData}
            onUpdate={(updatedData) => setUserData({ ...userData, ...updatedData })}
            sx={cardStyle}
          />

          {/* Linked Clients - hidden for linked clients (they don't manage other clients) */}
          {caregivers.length === 0 && (
          <Paper sx={cardStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Linked Clients ({clients.length})</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddClientDialog(true)}>
                  Add Client
                </Button>
              </Box>
              {clients.length === 0 ? (
                <Typography color="text.secondary">No clients linked</Typography>
              ) : (
                <List dense>
                  {clients.map((client) => {
                    const linkedAt = client.linked_at ? new Date(client.linked_at) : null;
                    const daysManaged = linkedAt ? Math.floor((new Date() - linkedAt) / (1000 * 60 * 60 * 24)) : 0;
                    return (
                    <ListItem key={client.id} sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1.5, borderBottom: '1px solid #eee' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight={500}>{client.name}</Typography>
                          {client.client_acknowledged ? (
                            <Chip label="Verified" color="success" size="small" />
                          ) : (
                            <Chip label="Pending" color="warning" size="small" />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View client account">
                            <IconButton size="small" onClick={() => navigate(`/users/${client.id}`)}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {client.email && (
                            <Tooltip title="Send reset email">
                              <IconButton size="small" onClick={async () => {
                                try {
                                  await adminAPI.sendPasswordReset(client.id);
                                  setSuccess(`Password reset sent to ${client.email}`);
                                } catch (err) {
                                  setError(err.response?.data?.error || 'Failed to send reset');
                                }
                              }}>
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!client.client_acknowledged && (
                            <Tooltip title="Manually verify">
                              <IconButton size="small" onClick={() => handleManuallyVerify(client.id)}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {client.phone && (
                            <Tooltip title={client.guide_sent_at ? `Guide last sent ${format(new Date(client.guide_sent_at), 'MMM d, yyyy')}` : 'Send quick start guide'}>
                              <IconButton
                                size="small"
                                color="info"
                                disabled={sendingGuide === client.id}
                                onClick={async () => {
                                  setSendingGuide(client.id);
                                  try {
                                    await adminAPI.sendGuideToClient(userId, client.id);
                                    setSuccess(`Quick start guide sent to ${client.name || client.phone}`);
                                    setTimeout(() => setSuccess(''), 5000);
                                  } catch (err) {
                                    setError(err.response?.data?.error || 'Failed to send guide');
                                  } finally {
                                    setSendingGuide(null);
                                  }
                                }}
                              >
                                {sendingGuide === client.id ? <CircularProgress size={18} /> : <MenuBookIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Unlink client">
                            <IconButton size="small" color="error" onClick={() => setRemoveClientDialog(client)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">{client.phone || 'No phone'}</Typography>
                        <Typography variant="caption" color="text.secondary">{client.relationship_type || 'owner'}</Typography>
                        {linkedAt && (
                          <Typography variant="caption" color="text.secondary">Added {format(linkedAt, 'MMM d, yyyy')}</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">{daysManaged}d managed</Typography>
                        <Typography variant="caption" color="text.secondary">{client.recent_activity_count || 0} events (30d)</Typography>
                      </Box>
                    </ListItem>
                    );
                  })}
                </List>
              )}
          </Paper>
          )}

          {/* Pending Verifications - hidden for linked clients */}
          {caregivers.length === 0 && (
          <Paper sx={cardStyle}>
            <Typography variant="h6" gutterBottom>Pending Verifications ({pendingVerifications.length})</Typography>
              {pendingVerifications.length === 0 ? (
                <Typography color="text.secondary">No pending verifications</Typography>
              ) : (
                <List dense>
                  {pendingVerifications.map((v) => (
                    <ListItem key={v.id}>
                      <ListItemText
                        primary={v.client_name}
                        secondary={`${v.status} • Expires: ${v.expires_at ? format(new Date(v.expires_at), 'PPp') : '-'}`}
                      />
                      <Chip label={v.status} size="small" color={v.status === 'pending' ? 'warning' : 'default'} />
                    </ListItem>
                  ))}
                </List>
              )}
          </Paper>
          )}
        </Box>
      </TabPanel>

      {/* Activity Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">User Activity</Typography>
            <TextField
              select
              size="small"
              label="Category"
              value={activityCategory}
              onChange={(e) => {
                setActivityCategory(e.target.value);
                loadActivity(e.target.value);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="analysis">Analysis (SMS/Voice)</MenuItem>
              <MenuItem value="account">Account</MenuItem>
              <MenuItem value="subscription">Subscription</MenuItem>
              <MenuItem value="clients">Clients</MenuItem>
              <MenuItem value="admin">Admin Actions</MenuItem>
              <MenuItem value="safelist">Safelist</MenuItem>
            </TextField>
          </Box>

          {activityLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : activity.length === 0 ? (
            <Typography color="text.secondary">No activity found</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>From</TableCell>
                    <TableCell>Content</TableCell>
                    <TableCell>Concern</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activity.map((event) => (
                    <TableRow
                      key={`${event.category || 'analysis'}-${event.id}`}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => setActivityDetailDialog(event)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getEventIcon(event.event_type, event.category)}
                          <Typography variant="body2">{event.event_type || 'message'}</Typography>
                          {event.category && event.category !== 'analysis' && (
                            <Chip label={event.category} size="small" variant="outlined" sx={{ ml: 0.5, textTransform: 'capitalize' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {event.from_phone
                          ? `***-***-${event.from_phone.slice(-4)}`
                          : event.actor_type && event.actor_type !== 'user'
                            ? <Chip label={event.actor_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                            : '-'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={event.content || event.transcription || ''}>
                          <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {event.content || event.transcription || event.summary || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {event.risk_level && (
                          <Chip label={getConcernLabel(event.risk_level)} size="small" color={getConcernColor(event.risk_level)} />
                        )}
                      </TableCell>
                      <TableCell>
                        {event.created_at ? format(new Date(event.created_at), 'PPp') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Admin Notes Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Admin Notes ({adminNotes.length})</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setNoteDialog(true)}>
              Add Note
            </Button>
          </Box>
          {adminNotes.length === 0 ? (
            <Typography color="text.secondary">No notes</Typography>
          ) : (
            <List>
              {adminNotes.map((note) => (
                <ListItem key={note.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px solid #eee' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{note.note}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {note.admin_name || note.admin_email} • {format(new Date(note.created_at), 'PPp')}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </TabPanel>

      {/* Billing History Tab */}
      <TabPanel value={tabValue} index={3}>
        {billingLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Recent Transactions */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Transactions</Typography>
                {billingHistory.charges.length === 0 ? (
                  <Typography color="text.secondary">No transactions found</Typography>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Payment Method</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Refunded</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {billingHistory.charges.map((charge) => (
                          <TableRow key={charge.id}>
                            <TableCell>{format(new Date(charge.created), 'MMM d, yyyy')}</TableCell>
                            <TableCell>{formatCurrency(charge.amount, charge.currency)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={charge.status} 
                                size="small" 
                                color={charge.status === 'succeeded' ? 'success' : 'default'} 
                              />
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{charge.payment_method || '-'}</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              {charge.refunded ? (
                                <Chip label={formatCurrency(charge.amount_refunded)} size="small" color="error" />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {charge.receipt_url && (
                                <Tooltip title="View receipt">
                                  <IconButton size="small" component={Link} href={charge.receipt_url} target="_blank">
                                    <ReceiptIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* Refunds */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Refunds</Typography>
                {billingHistory.refunds.length === 0 ? (
                  <Typography color="text.secondary">No refunds issued</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {billingHistory.refunds.map((refund) => (
                          <TableRow key={refund.id}>
                            <TableCell>{format(new Date(refund.created), 'MMM d, yyyy')}</TableCell>
                            <TableCell>{formatCurrency(refund.amount, refund.currency)}</TableCell>
                            <TableCell>
                              <Chip label={refund.status} size="small" color={refund.status === 'succeeded' ? 'success' : 'warning'} />
                            </TableCell>
                            <TableCell>{refund.reason || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* Invoices */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Invoices</Typography>
                {billingHistory.invoices.length === 0 ? (
                  <Typography color="text.secondary">No invoices found</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Number</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>PDF</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {billingHistory.invoices.map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell>{format(new Date(invoice.created), 'MMM d, yyyy')}</TableCell>
                            <TableCell>{invoice.number || '-'}</TableCell>
                            <TableCell>{formatCurrency(invoice.amount_paid, invoice.currency)}</TableCell>
                            <TableCell>
                              <Chip label={invoice.status} size="small" color={invoice.status === 'paid' ? 'success' : 'default'} />
                            </TableCell>
                            <TableCell>
                              {invoice.invoice_pdf && (
                                <IconButton size="small" component={Link} href={invoice.invoice_pdf} target="_blank">
                                  <DownloadIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <ScheduledTrainingPanel userId={userId} />
      </TabPanel>


      {/* Dialogs */}

      {/* Set Password Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)}>
        <DialogTitle>Set Temporary Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Temporary Password"
            value={tempPassword}
            onChange={(e) => setTempPassword(e.target.value)}
            margin="normal"
            type="text"
          />
          <FormControlLabel
            control={<Switch checked={notifyUser} onChange={(e) => setNotifyUser(e.target.checked)} />}
            label="Email password to user"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSetTempPassword} disabled={!tempPassword}>Set Password</Button>
        </DialogActions>
      </Dialog>

      {/* Update Quantity Dialog */}
      <Dialog open={subscriptionDialog} onClose={() => setSubscriptionDialog(false)}>
        <DialogTitle>Update Subscription Quantity</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Quantity"
            type="number"
            value={newQuantity}
            onChange={(e) => setNewQuantity(e.target.value)}
            margin="normal"
            inputProps={{ min: 1 }}
          />
          <TextField
            fullWidth
            label="Reason"
            value={quantityReason}
            onChange={(e) => setQuantityReason(e.target.value)}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateQuantity}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialog} onClose={() => setRefundDialog(false)}>
        <DialogTitle>Issue Refund</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Leave amount empty to refund the full amount of the most recent charge.
          </Typography>
          <TextField
            fullWidth
            label="Refund Amount"
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            margin="normal"
            placeholder="0.00"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            fullWidth
            label="Reason (internal note)"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            placeholder="Why is this refund being issued?"
          />
          
          {/* Show recent charges for reference */}
          {billingHistory.charges.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Recent Charges:</Typography>
              {billingHistory.charges.slice(0, 3).map(charge => (
                <Typography key={charge.id} variant="body2" color="text.secondary">
                  {format(new Date(charge.created), 'MMM d, yyyy')} - {formatCurrency(charge.amount)} 
                  {charge.refunded && ` (Refunded: ${formatCurrency(charge.amount_refunded)})`}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRefund}>
            {refundAmount ? `Refund ${formatCurrency(parseFloat(refundAmount) || 0)}` : 'Refund Full Amount'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog} onClose={() => setNoteDialog(false)}>
        <DialogTitle>Add Admin Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            margin="normal"
            multiline
            rows={4}
            placeholder="Internal note about this user..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddNote} disabled={!newNote.trim()}>Add Note</Button>
        </DialogActions>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={addClientDialog} onClose={() => setAddClientDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Linked Client</DialogTitle>
        <DialogContent>
          <Tabs value={addClientMode === 'search' ? 0 : 1} onChange={(e, v) => setAddClientMode(v === 0 ? 'search' : 'create')} sx={{ mb: 2 }}>
            <Tab label="Search Existing" />
            <Tab label="Create New" />
          </Tabs>

          {addClientMode === 'search' ? (
            <Box>
              <TextField
                fullWidth
                label="Search by email, phone, or name"
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  handleSearchClients(e.target.value);
                }}
                margin="normal"
                placeholder="Enter at least 2 characters..."
              />
              {clientSearchLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
              
              {clientSearchResults.length > 0 && (
                <List>
                  {clientSearchResults.map((user) => (
                    <ListItem 
                      key={user.id} 
                      button 
                      onClick={() => handleLinkClient(user.id)}
                      sx={{ border: '1px solid #eee', borderRadius: 1, mb: 1 }}
                    >
                      <ListItemText
                        primary={user.name || user.email}
                        secondary={`${user.email} • ${user.phone ? `***-***-${user.phone.slice(-4)}` : 'No phone'}`}
                      />
                      <Chip label="Link" size="small" color="primary" />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          ) : (
            <Box>
              <TextField
                fullWidth
                label="Name"
                value={newClientData.name}
                onChange={(e) => setNewClientData({ ...newClientData, name: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Phone"
                value={newClientData.phone}
                onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                margin="normal"
                required
                placeholder="(555) 555-5555"
              />
              <TextField
                fullWidth
                label="Email (optional)"
                value={newClientData.email}
                onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddClientDialog(false)}>Cancel</Button>
          {addClientMode === 'create' && (
            <Button variant="contained" onClick={handleCreateAndLinkClient} disabled={!newClientData.name || !newClientData.phone}>
              Create & Link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Remove Client Dialog */}
      <Dialog open={!!removeClientDialog} onClose={() => setRemoveClientDialog(null)}>
        <DialogTitle>Unlink Client</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unlink <strong>{removeClientDialog?.name}</strong> from this account?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will remove the relationship but not delete the client's user record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveClientDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleUnlinkClient}>Unlink</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialog} onClose={() => { setDeleteDialog(false); setDeleteConfirmText(''); setDeleteReason(''); }}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Account Permanently</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action is irreversible. The user's personal data will be anonymized, their Stripe subscription will be canceled, and they will lose access permanently.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Deleting <strong>{userData?.name || userData?.email}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Reason for deletion"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            placeholder="e.g. User requested account deletion"
          />
          <TextField
            fullWidth
            label='Type "DELETE" to confirm'
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            margin="normal"
            error={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE'}
            helperText='You must type "DELETE" exactly to proceed'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialog(false); setDeleteConfirmText(''); setDeleteReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={deleteConfirmText !== 'DELETE' || deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Trial Management Dialog */}
      <Dialog open={trialDialog} onClose={() => setTrialDialog(false)}>
        <DialogTitle>
          {userData?.subscription_status === 'trialing' ? 'Manage Trial Period' : 'Start Trial Period'}
        </DialogTitle>
        <DialogContent>
          {userData?.subscription_status === 'trialing' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Currently trialing. Stripe will handle the current trial end date.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Trial End Date"
            type="date"
            value={trialEndDate}
            onChange={(e) => setTrialEndDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date(Date.now() + 86400000).toISOString().split('T')[0] }}
            helperText="Must be a future date"
          />
          <TextField
            fullWidth
            label="Reason (internal note)"
            value={trialReason}
            onChange={(e) => setTrialReason(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            placeholder="e.g. Extended trial per customer request"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateTrialEnd} disabled={!trialEndDate}>
            Update Trial
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity Detail Dialog */}
      <Dialog open={!!activityDetailDialog} onClose={() => setActivityDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {activityDetailDialog && getEventIcon(activityDetailDialog.event_type, activityDetailDialog.category)}
              Event Details
            </Box>
            <IconButton size="small" onClick={() => setActivityDetailDialog(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {activityDetailDialog && (
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Event Type</Typography>
                <Typography>{activityDetailDialog.event_type || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography>{activityDetailDialog.created_at ? format(new Date(activityDetailDialog.created_at), 'PPpp') : '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">From Phone</Typography>
                <Typography>{activityDetailDialog.from_phone || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Concern Level</Typography>
                {activityDetailDialog.risk_level ? (
                  <Chip label={getConcernLabel(activityDetailDialog.risk_level)} size="small" color={getConcernColor(activityDetailDialog.risk_level)} />
                ) : (
                  <Typography>-</Typography>
                )}
              </Grid>
              {activityDetailDialog.call_direction && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Call Direction</Typography>
                  <Typography sx={{ textTransform: 'capitalize' }}>{activityDetailDialog.call_direction}</Typography>
                </Grid>
              )}
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {activityDetailDialog.is_flagged && <Chip label="Flagged" size="small" color="warning" />}
                  {activityDetailDialog.is_read ? (
                    <Chip label="Read" size="small" variant="outlined" />
                  ) : (
                    <Chip label="Unread" size="small" color="info" />
                  )}
                </Box>
              </Grid>
              {activityDetailDialog.user_name && activityDetailDialog.user_id !== userId && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">Client</Typography>
                  <Typography>{activityDetailDialog.user_name}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Content / Transcription</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f9f9f9' }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {activityDetailDialog.content || activityDetailDialog.transcription || '-'}
                  </Typography>
                </Paper>
              </Grid>
              {activityDetailDialog.summary && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">AI Summary / Response</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f5f5ff' }}>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      {activityDetailDialog.summary}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {activityDetailDialog.extracted_signals && Object.keys(activityDetailDialog.extracted_signals).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Extracted Signals</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#fff8f0' }}>
                    {Array.isArray(activityDetailDialog.extracted_signals) ? (
                      activityDetailDialog.extracted_signals.map((signal, idx) => (
                        <Chip
                          key={idx}
                          label={typeof signal === 'string' ? signal : signal.type || signal.name || JSON.stringify(signal)}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                          color={signal.level === 'HIGH' ? 'error' : signal.level === 'MODERATE' ? 'warning' : 'default'}
                        />
                      ))
                    ) : (
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                        {JSON.stringify(activityDetailDialog.extracted_signals, null, 2)}
                      </pre>
                    )}
                  </Paper>
                </Grid>
              )}
              {activityDetailDialog.metadata && Object.keys(activityDetailDialog.metadata).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Metadata</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f5f5f5' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {JSON.stringify(activityDetailDialog.metadata, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityDetailDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetail;